import React from 'react'
import { Logger } from '../Logger';

export const CookieUtils = {
    "getCookie" : (name:string) => {
        const value = `; ${document.cookie}`;
        const parts = value.split(`; ${name}=`);
        if (parts.length === 2) {
            const cookieValue = parts.pop()?.split(';').shift();
            return cookieValue;
        } else {
            // Try alternative parsing method
            const cookies = document.cookie.split(';');
            for (let cookie of cookies) {
                cookie = cookie.trim();
                if (cookie.startsWith(name + '=')) {
                    const cookieValue = cookie.substring(name.length + 1);
                    return cookieValue;
                }
            }
            
            return null;
        }
    },
    "getCookieFromMultipleDomains" : (name:string) => {
        // Try to get cookie from current domain first
        let cookieValue = CookieUtils.getCookie(name);
        if (cookieValue) {
            Logger.debug("st-ck-client/src/utils/CookieUtils/CookieUtils.ts", `CookieUtils.getCookieFromMultipleDomains(${name}): Found in current domain: ${cookieValue}`);
            return cookieValue;
        }
        
        // If not found, try to get from parent domain (.campaignking.ai)
        // Note: This is a workaround since we can't directly read cookies from other domains
        // But we can check if the cookie was set with the right domain attributes
        Logger.debug("st-ck-client/src/utils/CookieUtils/CookieUtils.ts", `CookieUtils.getCookieFromMultipleDomains(${name}): Cookie not found in current domain, checking all cookies: ${document.cookie}`);
        
        // Return null if not found - the server should handle cross-domain cookie validation
        return null;
    },
    "setCookie" : (name:string, value:string, days:number, domain?:string) => {
        let expires = "";
        if (days) {
            const date = new Date();
            if (days === 0) {
                expires = "; expires=0";
            } else {
                date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
                expires = `; expires=${date.toUTCString()}`;
            }
        }
        
        let domainAttr = "";
        if (domain) {
            domainAttr = `; domain=${domain}`;
        }
        
        const cookieString = `${name}=${value || ""}${expires}; path=/${domainAttr}`;
        document.cookie = cookieString;
    },
    "setCrossDomainCookie" : (name:string, value:string, domain:string, days:number = 0) => {
        let expires = "";
        if (days) {
            const date = new Date();
            if (days === 0) {
                expires = "; expires=0";
            } else {
                date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
                expires = `; expires=${date.toUTCString()}`;
            }
        }
        
        // Set cookie with cross-domain attributes
        const cookieString = `${name}=${value}${expires}; path=/; domain=${domain}; secure; samesite=none`;
        document.cookie = cookieString;
        
        // Verify the cookie was set (only log if there's an issue)
        setTimeout(() => {
            const verifyCookie = CookieUtils.getCookie(name);
            if (verifyCookie !== value) {
                Logger.debug("st-ck-client/src/utils/CookieUtils/CookieUtils.ts", `CookieUtils.setCrossDomainCookie: Verification failed - cookie ${name} value: ${verifyCookie}`);
            }
        }, 100);
    },
    "syncCookiesBetweenDomains" : (name:string, value:string) => {
        // Get current domain
        const currentDomain = window.location.hostname;
        
        // Set cookie for current domain (portal.campaignking.ai)
        CookieUtils.setCookie(name, value, 0);
        
        // Set cookie for parent domain (.campaignking.ai) - this should be accessible by both subdomains
        const parentDomain = currentDomain.split('.').slice(-2).join('.');
        if (parentDomain !== currentDomain) {
            // Try multiple approaches to ensure the cookie is set correctly
            // Approach 1: Standard cross-domain cookie
            CookieUtils.setCrossDomainCookie(name, value, `.${parentDomain}`, 0);
            
            // Approach 2: Direct cookie setting with explicit attributes
            const expires = new Date(Date.now() + 24 * 60 * 60 * 1000).toUTCString(); // 24 hours
            document.cookie = `${name}=${value}; expires=${expires}; path=/; domain=.${parentDomain}; secure; samesite=none`;
            
            // Approach 3: Try without secure flag (in case of HTTP)
            if (window.location.protocol === 'http:') {
                document.cookie = `${name}=${value}; expires=${expires}; path=/; domain=.${parentDomain}; samesite=none`;
            }
        }
        
        // Set cookie for specific subdomains
        CookieUtils.setCookie(name, value, 0, 'apigateway.campaignking.ai');
        
        // Verify synchronization (only log if there's an issue)
        setTimeout(() => {
            const currentDomainCookie = CookieUtils.getCookie(name);
            
            // Only log if synchronization failed
            if (currentDomainCookie !== value) {
                Logger.debug("st-ck-client/src/utils/CookieUtils/CookieUtils.ts", `CookieUtils.syncCookiesBetweenDomains(${name}): Cookie synchronization failed`);
            }
        }, 200);
    },
    "deleteCookie" : (name:string, domain?:string) => {
        if (domain) {
            // Delete cookie from specific domain
            document.cookie = `${name}=; Max-Age=-99999999; domain=${domain}; path=/`;
            Logger.debug("st-ck-client/src/utils/CookieUtils/CookieUtils.ts", `CookieUtils.deleteCookie: Deleted cookie ${name} from domain ${domain}`);
        } else {
            // Delete cookie from current domain
            document.cookie = `${name}=; Max-Age=-99999999;`;
            Logger.debug("st-ck-client/src/utils/CookieUtils/CookieUtils.ts", `CookieUtils.deleteCookie: Deleted cookie ${name} from current domain`);
        }
    },
    "deleteCookieFromAllDomains" : (name:string) => {
        Logger.debug("st-ck-client/src/utils/CookieUtils/CookieUtils.ts", `CookieUtils.deleteCookieFromAllDomains(${name}): Deleting cookie from all possible domains`);
        
        // Delete from current domain
        CookieUtils.deleteCookie(name);
        
        // Delete from parent domain (.campaignking.ai)
        CookieUtils.deleteCookie(name, '.campaignking.ai');
        
        // Delete from specific subdomains
        CookieUtils.deleteCookie(name, 'portal.campaignking.ai');
        CookieUtils.deleteCookie(name, 'apigateway.campaignking.ai');
        
        // Try to delete with different path variations
        document.cookie = `${name}=; Max-Age=-99999999; path=/`;
        document.cookie = `${name}=; Max-Age=-99999999; path=/; domain=.campaignking.ai`;
        document.cookie = `${name}=; Max-Age=-99999999; path=/; domain=portal.campaignking.ai`;
        document.cookie = `${name}=; Max-Age=-99999999; path=/; domain=apigateway.campaignking.ai`;
        
        // Also try with secure and samesite attributes
        document.cookie = `${name}=; Max-Age=-99999999; path=/; domain=.campaignking.ai; secure; samesite=none`;
        
        Logger.debug("st-ck-client/src/utils/CookieUtils/CookieUtils.ts", `CookieUtils.deleteCookieFromAllDomains(${name}): Attempted deletion from all domains`);
        
        // Verify deletion
        setTimeout(() => {
            const remainingCookie = CookieUtils.getCookie(name);
            Logger.debug("st-ck-client/src/utils/CookieUtils/CookieUtils.ts", `CookieUtils.deleteCookieFromAllDomains(${name}): Verification - remaining cookie: ${remainingCookie}`);
        }, 100);
    },
    "expireCookieOnAllDomains" : (name:string) => {
        Logger.debug("st-ck-client/src/utils/CookieUtils/CookieUtils.ts", `CookieUtils.expireCookieOnAllDomains(${name}): Expiring cookie on all domains by setting past expiration`);
        
        // Set cookie to expire in the past (Jan 1, 1970) on all domains
        const pastDate = new Date(0).toUTCString();
        
        // Expire on current domain
        document.cookie = `${name}=; expires=${pastDate}; path=/`;
        
        // Expire on parent domain (.campaignking.ai)
        document.cookie = `${name}=; expires=${pastDate}; path=/; domain=.campaignking.ai`;
        
        // Expire on specific subdomains
        document.cookie = `${name}=; expires=${pastDate}; path=/; domain=portal.campaignking.ai`;
        document.cookie = `${name}=; expires=${pastDate}; path=/; domain=apigateway.campaignking.ai`;
        
        // Expire with secure and samesite attributes for .campaignking.ai
        document.cookie = `${name}=; expires=${pastDate}; path=/; domain=.campaignking.ai; secure; samesite=none`;
        
        // Also try setting to empty value with past expiration
        document.cookie = `${name}=; expires=${pastDate}; path=/; domain=.campaignking.ai; secure; samesite=none`;
        
        Logger.debug("st-ck-client/src/utils/CookieUtils/CookieUtils.ts", `CookieUtils.expireCookieOnAllDomains(${name}): Attempted to expire cookie on all domains`);
        
        // Verify expiration
        setTimeout(() => {
            const remainingCookie = CookieUtils.getCookie(name);
            Logger.debug("st-ck-client/src/utils/CookieUtils/CookieUtils.ts", `CookieUtils.expireCookieOnAllDomains(${name}): Verification - remaining cookie: ${remainingCookie}`);
        }, 100);
    },
    
    "forceOverwriteCookieOnAllDomains" : (name:string, value:string, days:number = 0) => {
        Logger.debug("st-ck-client/src/utils/CookieUtils/CookieUtils.ts", `CookieUtils.forceOverwriteCookieOnAllDomains(${name}): Force overwriting cookie on all domains with value: ${value}`);
        
        // CRITICAL: First expire ALL existing cookies to clear any stale values
        CookieUtils.expireCookieOnAllDomains(name);
        
        // Wait for expiration to take effect, then set new cookies
        setTimeout(() => {
            Logger.debug("st-ck-client/src/utils/CookieUtils/CookieUtils.ts", `CookieUtils.forceOverwriteCookieOnAllDomains(${name}): Setting new cookies after expiration`);
            
            // Set on current domain
            CookieUtils.setCookie(name, value, days);
            
            // Set on parent domain with multiple attribute combinations
            const parentDomain = '.campaignking.ai';
            
            // Method 1: Standard cross-domain cookie
            CookieUtils.setCrossDomainCookie(name, value, parentDomain, days);
            
            // Method 2: Direct with explicit attributes
            document.cookie = `${name}=${value}; max-age=${days * 24 * 60 * 60}; path=/; domain=${parentDomain}; secure; samesite=none`;
            
            // Method 3: Alternative attribute combination
            document.cookie = `${name}=${value}; max-age=${days * 24 * 60 * 60}; path=/; domain=${parentDomain}; secure`;
            
            // Method 4: Without secure flag (in case HTTPS context has issues)
            document.cookie = `${name}=${value}; max-age=${days * 24 * 60 * 60}; path=/; domain=${parentDomain}`;
            
            // Set on specific subdomains
            CookieUtils.setCookie(name, value, days, 'portal.campaignking.ai');
            CookieUtils.setCookie(name, value, days, 'apigateway.campaignking.ai');
            
            Logger.debug("st-ck-client/src/utils/CookieUtils/CookieUtils.ts", `CookieUtils.forceOverwriteCookieOnAllDomains(${name}): Force overwrite complete`);
            
            // Verify the overwrite worked
            setTimeout(() => {
                const currentDomainCookie = CookieUtils.getCookie(name);
                Logger.debug("st-ck-client/src/utils/CookieUtils/CookieUtils.ts", `CookieUtils.forceOverwriteCookieOnAllDomains(${name}): Verification - current domain cookie: ${currentDomainCookie}`);
                
                // Also check if we can access the parent domain cookie
                const allCookies = document.cookie;
                Logger.debug("st-ck-client/src/utils/CookieUtils/CookieUtils.ts", `CookieUtils.forceOverwriteCookieOnAllDomains(${name}): Force overwrite complete - all cookies after overwrite: ${allCookies}`);
            }, 100);
        }, 150); // Wait longer for expiration to complete
    },
    
    "forceExpireParentDomainCookie" : (name:string) => {
        Logger.debug("st-ck-client/src/utils/CookieUtils/CookieUtils.ts", `CookieUtils.forceExpireParentDomainCookie(${name}): Aggressively expiring parent domain cookie`);
        
        // This method specifically targets the .campaignking.ai cookie deletion issue
        const pastDate = new Date(0).toUTCString();
        const parentDomain = '.campaignking.ai';
        
        // Try multiple approaches to expire the parent domain cookie
        
        // Method 1: Standard expiration
        document.cookie = `${name}=; expires=${pastDate}; path=/; domain=${parentDomain}`;
        
        // Method 2: With secure flag
        document.cookie = `${name}=; expires=${pastDate}; path=/; domain=${parentDomain}; secure`;
        
        // Method 3: With samesite attribute
        document.cookie = `${name}=; expires=${pastDate}; path=/; domain=${parentDomain}; samesite=none`;
        
        // Method 4: With both secure and samesite
        document.cookie = `${name}=; expires=${pastDate}; path=/; domain=${parentDomain}; secure; samesite=none`;
        
        // Method 5: Set to empty value with past expiration
        document.cookie = `${name}=; expires=${pastDate}; path=/; domain=${parentDomain}`;
        
        // Method 6: Try with different paths
        document.cookie = `${name}=; expires=${pastDate}; path=/; domain=${parentDomain}`;
        document.cookie = `${name}=; expires=${pastDate}; path=/api; domain=${parentDomain}`;
        document.cookie = `${name}=; expires=${pastDate}; path=/auth; domain=${parentDomain}`;
        
        // Method 7: Try setting to a dummy value that will be ignored
        document.cookie = `${name}=EXPIRED; expires=${pastDate}; path=/; domain=${parentDomain}`;
        
        Logger.debug("st-ck-client/src/utils/CookieUtils/CookieUtils.ts", `CookieUtils.forceExpireParentDomainCookie(${name}): Attempted multiple methods to expire parent domain cookie`);
        
        // Verify if any method worked
        setTimeout(() => {
            const allCookies = document.cookie;
            Logger.debug("st-ck-client/src/utils/CookieUtils/CookieUtils.ts", `CookieUtils.forceExpireParentDomainCookie(${name}): All cookies after aggressive expiration: ${allCookies}`);
            
            // Check if the parent domain cookie is still accessible
            if (allCookies.includes(`${name}=`)) {
                Logger.debug("st-ck-client/src/utils/CookieUtils/CookieUtils.ts", `CookieUtils.forceExpireParentDomainCookie(${name}): ⚠️ Parent domain cookie may still exist`);
            } else {
                Logger.debug("st-ck-client/src/utils/CookieUtils/CookieUtils.ts", `CookieUtils.forceExpireParentDomainCookie(${name}): ✅ Parent domain cookie appears to be expired`);
            }
        }, 200);
    },
    
    "validateAndRefreshCookies" : (name:string, expectedValue:string) => {
        Logger.debug("st-ck-client/src/utils/CookieUtils/CookieUtils.ts", `CookieUtils.validateAndRefreshCookies(${name}): Validating and refreshing cookies if needed`);
        
        // Check current cookie value
        const currentCookie = CookieUtils.getCookie(name);
        Logger.debug("st-ck-client/src/utils/CookieUtils/CookieUtils.ts", `CookieUtils.validateAndRefreshCookies(${name}): Current cookie value: ${currentCookie}`);
        Logger.debug("st-ck-client/src/utils/CookieUtils/CookieUtils.ts", `CookieUtils.validateAndRefreshCookies(${name}): Expected value: ${expectedValue}`);
        
        // If cookies don't match or are missing, refresh them
        if (currentCookie !== expectedValue) {
            Logger.debug("st-ck-client/src/utils/CookieUtils/CookieUtils.ts", `CookieUtils.validateAndRefreshCookies(${name}): Cookie mismatch detected, refreshing cookies`);
            
            // Use the safe synchronization method that doesn't clear cookies first
            CookieUtils.syncCookiesBetweenDomains(name, expectedValue);
            
            // Verify the refresh worked
            setTimeout(() => {
                const refreshedCookie = CookieUtils.getCookie(name);
                Logger.debug("st-ck-client/src/utils/CookieUtils/CookieUtils.ts", `CookieUtils.validateAndRefreshCookies(${name}): After refresh - cookie value: ${refreshedCookie}`);
                
                if (refreshedCookie === expectedValue) {
                    Logger.debug("st-ck-client/src/utils/CookieUtils/CookieUtils.ts", `CookieUtils.validateAndRefreshCookies(${name}): ✅ Cookie refresh successful`);
                } else {
                    Logger.debug("st-ck-client/src/utils/CookieUtils/CookieUtils.ts", `CookieUtils.validateAndRefreshCookies(${name}): ⚠️ Cookie refresh may have failed`);
                }
            }, 100);
        } else {
            Logger.debug("st-ck-client/src/utils/CookieUtils/CookieUtils.ts", `CookieUtils.validateAndRefreshCookies(${name}): ✅ Cookies are already valid, no refresh needed`);
        }
    },
    "getAllCookies" : () => {
        Logger.debug("st-ck-client/src/utils/CookieUtils/CookieUtils.ts", `CookieUtils.getAllCookies: All cookies: ${document.cookie}`);
        return document.cookie;
    },
    "checkCookieAccessibility" : (name:string) => {
        Logger.debug("st-ck-client/src/utils/CookieUtils/CookieUtils.ts", `CookieUtils.checkCookieAccessibility(${name}): Checking cookie accessibility`);
        Logger.debug("st-ck-client/src/utils/CookieUtils/CookieUtils.ts", `CookieUtils.checkCookieAccessibility(${name}): Current URL: ${window.location.href}`);
        Logger.debug("st-ck-client/src/utils/CookieUtils/CookieUtils.ts", `CookieUtils.checkCookieAccessibility(${name}): Protocol: ${window.location.protocol}`);
        Logger.debug("st-ck-client/src/utils/CookieUtils/CookieUtils.ts", `CookieUtils.checkCookieAccessibility(${name}): Hostname: ${window.location.hostname}`);
        Logger.debug("st-ck-client/src/utils/CookieUtils/CookieUtils.ts", `CookieUtils.checkCookieAccessibility(${name}): Port: ${window.location.port}`);
        
        // Check if we're in a secure context
        if (window.location.protocol === 'https:') {
            Logger.debug("st-ck-client/src/utils/CookieUtils/CookieUtils.ts", `CookieUtils.checkCookieAccessibility(${name}): Running in HTTPS context`);
        } else {
            Logger.debug("st-ck-client/src/utils/CookieUtils/CookieUtils.ts", `CookieUtils.checkCookieAccessibility(${name}): Running in HTTP context - this might cause issues with Secure cookies`);
        }
        
        // Try to read the cookie
        const cookieValue = CookieUtils.getCookie(name);
        Logger.debug("st-ck-client/src/utils/CookieUtils/CookieUtils.ts", `CookieUtils.checkCookieAccessibility(${name}): Cookie value: ${cookieValue}`);
        
        return cookieValue;
    },
    "syncCookieToCurrentDomain" : (name:string, value:string, days:number = 0) => {
        Logger.debug("st-ck-client/src/utils/CookieUtils/CookieUtils.ts", `CookieUtils.syncCookieToCurrentDomain(${name}): Syncing cookie to current domain`);
        Logger.debug("st-ck-client/src/utils/CookieUtils/CookieUtils.ts", `CookieUtils.syncCookieToCurrentDomain(${name}): Current domain: ${window.location.hostname}`);
        Logger.debug("st-ck-client/src/utils/CookieUtils/CookieUtils.ts", `CookieUtils.syncCookieToCurrentDomain(${name}): Cookie value: ${value}`);
        
        // Always ensure the cookie is set for the current domain
        CookieUtils.setCookie(name, value, days);
        
        // Also set for the parent domain for cross-subdomain access
        const parentDomain = window.location.hostname.split('.').slice(-2).join('.');
        if (parentDomain !== window.location.hostname) {
            Logger.debug("st-ck-client/src/utils/CookieUtils/CookieUtils.ts", `CookieUtils.syncCookieToCurrentDomain(${name}): Setting for parent domain: ${parentDomain}`);
            CookieUtils.setCrossDomainCookie(name, value, `.${parentDomain}`, days);
        }
        
        // Verify the cookie was set for current domain
        setTimeout(() => {
            const currentDomainCookie = CookieUtils.getCookie(name);
            Logger.debug("st-ck-client/src/utils/CookieUtils/CookieUtils.ts", `CookieUtils.syncCookieToCurrentDomain(${name}): Verification - current domain cookie: ${currentDomainCookie}`);
        }, 100);
    },
    "ensureCookieAccessibility" : (name:string) => {
        Logger.debug("st-ck-client/src/utils/CookieUtils/CookieUtils.ts", `CookieUtils.ensureCookieAccessibility(${name}): Ensuring cookie is accessible on current domain`);
        
        // First try to read the cookie
        let cookieValue = CookieUtils.getCookie(name);
        
        if (cookieValue) {
            Logger.debug("st-ck-client/src/utils/CookieUtils/CookieUtils.ts", `CookieUtils.ensureCookieAccessibility(${name}): Cookie already accessible: ${cookieValue}`);
            return cookieValue;
        }
        
        // If not accessible, try to get from sessionStorage as fallback
        if (typeof sessionStorage !== 'undefined') {
            const sessionStorageValue = sessionStorage.getItem(name);
            if (sessionStorageValue) {
                Logger.debug("st-ck-client/src/utils/CookieUtils/CookieUtils.ts", `CookieUtils.ensureCookieAccessibility(${name}): Found in sessionStorage, syncing to cookie: ${sessionStorageValue}`);
                CookieUtils.syncCookieToCurrentDomain(name, sessionStorageValue, 0);
                return sessionStorageValue;
            }
        }
        
        Logger.debug("st-ck-client/src/utils/CookieUtils/CookieUtils.ts", `CookieUtils.ensureCookieAccessibility(${name}): Cookie not accessible and no fallback found`);
        return null;
    },
    "monitorCookieChanges" : (name:string) => {
        Logger.debug("st-ck-client/src/utils/CookieUtils/CookieUtils.ts", `CookieUtils.monitorCookieChanges(${name}): Starting cookie monitoring`);
        
        let lastCookieValue = CookieUtils.getCookie(name);
        Logger.debug("st-ck-client/src/utils/CookieUtils/CookieUtils.ts", `CookieUtils.monitorCookieChanges(${name}): Initial cookie value: ${lastCookieValue}`);
        
        // Monitor cookie changes every second
        const monitorInterval = setInterval(() => {
            const currentCookieValue = CookieUtils.getCookie(name);
            
            if (currentCookieValue !== lastCookieValue) {
                Logger.debug("st-ck-client/src/utils/CookieUtils/CookieUtils.ts", `CookieUtils.monitorCookieChanges(${name}): Cookie value changed!`);
                Logger.debug("st-ck-client/src/utils/CookieUtils/CookieUtils.ts", `CookieUtils.monitorCookieChanges(${name}): Previous: ${lastCookieValue}, Current: ${currentCookieValue}`);
                
                if (lastCookieValue && !currentCookieValue) {
                    Logger.debug("st-ck-client/src/utils/CookieUtils/CookieUtils.ts", `CookieUtils.monitorCookieChanges(${name}): WARNING: Cookie was deleted!`);
                    Logger.debug("st-ck-client/src/utils/CookieUtils/CookieUtils.ts", `CookieUtils.monitorCookieChanges(${name}): Stack trace: ${new Error().stack}`);
                }
                
                lastCookieValue = currentCookieValue;
            }
        }, 1000);
        
        // Return function to stop monitoring
        return () => clearInterval(monitorInterval);
    }
};